import React from 'react';
import { UseFormMethods } from 'react-hook-form';
import Wahanda from 'common/wahanda';
import App from 'common/backbone-app';

import { useMutation } from 'react-query';
import { post } from 'reduxStore/apiService';
import apiUrl from 'common/api-url';

import { Notice } from 'components/common/Notice';
import { Button } from 'components/common/Button';
import { Form, Checkbox, Input } from 'components/common/Form';
import Loader from 'components/common/Loader';

import { LoginAnalytics } from '../tracking';
import { validations } from './validations';

import style from './LoginForm.scss';
import { Captcha } from '../Captcha';
import { BE_TURNSTILE_TOKEN_NAME } from '../Captcha/constants';
import { FormValues } from './types';
import { useEventListenerManager } from './useEventListenerManager';
import { LoginPassword } from './LoginPassword';
import { getSignupUrl } from './signupUrl';

interface Props {
  route: string;
}

export enum FormResponses {
  ACCOUNT_AUTHENTICATED = 'ACCOUNT_AUTHENTICATED',
  PROFILE_AUTHENTICATED = 'PROFILE_AUTHENTICATED',
  LOCKED = 'LOCKED',
  REQUIRES_RESET = 'REQUIRES_RESET',
  NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
  NOT_VERIFIED_CAPTCHA = 'NOT_VERIFIED_CAPTCHA',
}

export const LoginForm: React.FC<Props> = ({ route }) => {
  const [errorMessage, setErrorMessage] = React.useState();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [errorType, setErrorType] = React.useState<FormResponses>();
  const errorMessageRef = React.useRef<HTMLDivElement>(null);
  const forgotPasswordLink = App.getPortalRootUrl() + Wahanda.lang.login.links.requestPassword;
  const errorsWithResetLink = [FormResponses.REQUIRES_RESET, FormResponses.LOCKED];
  const onSubmitListenerManager = useEventListenerManager<(isError: boolean) => void>();
  const formMethodsRef = React.useRef<UseFormMethods<FormValues> | null>(null);

  const signUpLink = getSignupUrl();

  const onSuccess = (response) => {
    const isError = [
      FormResponses.ACCOUNT_AUTHENTICATED,
      FormResponses.PROFILE_AUTHENTICATED,
    ].includes(response.result)
      ? false
      : true;
    setErrorType(response.result);

    if (response.result !== FormResponses.NOT_VERIFIED_CAPTCHA) {
      formMethodsRef.current?.clearErrors(BE_TURNSTILE_TOKEN_NAME);
    }

    switch (response.result) {
      case FormResponses.ACCOUNT_AUTHENTICATED:
      case FormResponses.PROFILE_AUTHENTICATED:
        setErrorMessage(undefined);
        redirectUser();
        break;
      case FormResponses.LOCKED:
        setErrorMessage(Wahanda.lang.login.lockedErrorMessage);
        break;
      case FormResponses.REQUIRES_RESET:
        setErrorMessage(Wahanda.lang.login.resetErrorMessage);
        break;
      case FormResponses.NOT_VERIFIED_CAPTCHA:
        LoginAnalytics.trackErrorPassword();
        setErrorMessage(Wahanda.lang.login.unauthorisedErrorMessage);
        formMethodsRef.current?.setError(BE_TURNSTILE_TOKEN_NAME, {
          type: 'server',
          message: Wahanda.lang.validate.defaults.required,
        });
        break;
      case FormResponses.NOT_AUTHENTICATED:
        LoginAnalytics.trackErrorPassword();
        setErrorMessage(Wahanda.lang.login.unauthorisedErrorMessage);
        break;
      default:
        LoginAnalytics.trackErrorPassword();
        setErrorMessage(Wahanda.lang.login.unauthorisedErrorMessage);
        break;
    }

    onSubmitListenerManager.emit(isError);
  };

  React.useLayoutEffect(() => {
    if (errorMessage && errorsWithResetLink.includes(errorType!)) {
      const link: HTMLAnchorElement | undefined = errorMessageRef?.current?.getElementsByTagName(
        'a',
      )[0];

      link?.setAttribute('href', forgotPasswordLink);
    }
  }, [errorMessage, errorType]);

  const redirectUser = () => {
    const config = new App.Models.Config();
    setIsRedirecting(true);

    config.fetch({
      success: () => {
        // Set the config to be used by libs requiring it
        App.config = config;

        Wahanda.Tracking.clearIdentify();
        Wahanda.Tracking.clearGroup();

        if (!config.contentChannelMatches(App.domainChannelCode())) {
          LoginAnalytics.trackLoginSubmit();

          let uriComponent = decodeURIComponent(route);
          if (uriComponent === 'undefined') {
            uriComponent = '';
          }
          // Content channel is different than the user is on now. Redirect.
          window.location = (App.getContentChannelUrl(config.getContentChannel()) +
            uriComponent) as any;
          return;
        }

        let url;
        if (config.get('venue')) {
          if (App.isTaxAuditor()) {
            url = Wahanda.Url.getFullUrl('reports', 'transactions');
          } else if (route) {
            url = decodeURIComponent(route);
          } else {
            url = Wahanda.Url.getFullUrl('calendar');
          }

          LoginAnalytics.trackLoginSubmit();
          window.location.href = url;
        } else {
          setIsRedirecting(false);
          LoginAnalytics.trackErrorNoVenues();
          setErrorMessage(Wahanda.lang.login.noVenuesErrorMessage);
        }
      },
      error: () => {
        setIsRedirecting(false);
        LoginAnalytics.trackErrorNoVenues();
        setErrorMessage(Wahanda.lang.login.noVenuesErrorMessage);
      },
    });
  };

  const [mutate, { isLoading }] = useMutation(
    (formData) => post(apiUrl('AUTHENTICATION'), formData),
    { onSuccess },
  );

  const handleSubmit = (values) => {
    LoginAnalytics.trackLoginClick();
    mutate({ ...values, persistentLogin: values.persistentLogin || false });
  };

  const showSalonBoosterMigration = () => {
    return document.location.search.indexOf('sbMigration') > -1;
  };

  const handleRegisterRedirect = () => {
    LoginAnalytics.trackSignupClick();
  };

  const handleForgotPasswordRedirect = () => {
    LoginAnalytics.trackForgotClick();
  };

  return (
    <>
      {errorMessage && (
        <div ref={errorMessageRef}>
          <Notice
            type="alert"
            dataTest={`error-message-${errorType}`}
            message={<div dangerouslySetInnerHTML={{ __html: errorMessage! }} />}
          />
        </div>
      )}
      {showSalonBoosterMigration() && (
        <Notice
          className={style.errorMessage}
          message={Wahanda.lang.login.salonboosterMigrationMessage}
        />
      )}
      <div className={style.login}>
        <Form<FormValues> onSubmit={handleSubmit}>
          {(methods) => {
            formMethodsRef.current = methods;
            const { errors, getValues } = methods;
            const fields = validations({ errors });
            const areFieldsBlank =
              errors.user &&
              errors.password &&
              getValues('user') === undefined &&
              getValues('password') === undefined;

            if (areFieldsBlank) {
              LoginAnalytics.trackInvalidFields();
            }

            return (
              <>
                <div className={style.field}>
                  <Input {...fields.user} />
                </div>
                <LoginPassword
                  passwordField={fields.password}
                  onSubmitListenerManager={onSubmitListenerManager}
                />

                <Captcha onSubmitListenerManager={onSubmitListenerManager} />

                <div className={style.remember}>
                  <Checkbox
                    name="persistentLogin"
                    label={Wahanda.lang.login.rememberUserText}
                    labelClassName={style.persistentLogin}
                  />
                  <a
                    className={style.forgotLink}
                    href={forgotPasswordLink}
                    onClick={handleForgotPasswordRedirect}
                  >
                    {Wahanda.lang.login.forgotPassword}
                  </a>
                </div>
                <Button
                  dataTest="login-form-submit"
                  label={Wahanda.lang.login.logInButton}
                  type="submit"
                  size="large"
                  fullWidth
                />
              </>
            );
          }}
        </Form>
      </div>
      <div className={style.signup}>
        <div className={style.signupText}>{Wahanda.lang.login.registerText}</div>
        <Button
          label={Wahanda.lang.login.registerButton}
          href={signUpLink}
          onClick={handleRegisterRedirect}
          size="large"
          fullWidth
          variant="secondary"
        />
      </div>
      {(isLoading || isRedirecting) && <Loader positionAbsolute />}
    </>
  );
};
